<script setup>
  import { ref, onMounted, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { IndexPageLayout } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { useSurveyCompletionStore } from "@stores/survey/surveyCompletion";
  import { useCompletionFilters } from "@/utils/composables/filters/useCompletionFilters";
  import CompletionKpis from "@/components/Survey/Progress/CompletionKpis.vue";
  import CompletionChart from "@/components/Survey/Progress/CompletionChart.vue";
  import CompletionLossesTable from "@/components/Survey/Progress/CompletionLossesTable.vue";
  import { completionUnavailableMessage } from "@/utils/completionUnavailable";
  import { describeFilterOptions } from "@/utils/describeFilterOptions";
  import { useAssistantPageState } from "@/composables/useAssistantPageState";

  const surveyCompletionStore = useSurveyCompletionStore();
  const {
    surveyCompletionData,
    cardsCompletionData,
    isCardDataAvailable,
    unavailableReason,
    completionFilters,
    filtersCountryOptions,
    filtersTargetOptions,
    filtersMasterCampaignsOptions,
    filtersCampaignsOptions,
    filtersAudiencesOptions,
    filtersGenderOptions,
  } = storeToRefs(surveyCompletionStore);

  useAssistantPageState(() => ({
    countries: describeFilterOptions(completionFilters.value.countries, filtersCountryOptions.value),
    targets: describeFilterOptions(completionFilters.value.targets, filtersTargetOptions.value),
    masterCampaigns: describeFilterOptions(
      completionFilters.value.master_campaigns,
      filtersMasterCampaignsOptions.value,
    ),
    campaigns: describeFilterOptions(completionFilters.value.campaigns, filtersCampaignsOptions.value),
    audiences: describeFilterOptions(completionFilters.value.audiences, filtersAudiencesOptions.value),
    genders: describeFilterOptions(completionFilters.value.genders, filtersGenderOptions.value),
  }));
  const { fetchSurveyCompletionData, resetCompletionData } = surveyCompletionStore;

  const { filterConfigs, filterValues, applyFilters, clearFilters } = useCompletionFilters();

  const isLoading = ref(false);

  const load = async (action) => {
    isLoading.value = true;
    await action();
    isLoading.value = false;
  };

  onMounted(() => load(fetchSurveyCompletionData));
  onUnmounted(() => resetCompletionData());
</script>

<template>
  <IndexPageLayout title="Completion">
    <template #filters>
      <FilterDropdown
        v-model="filterValues"
        :filters="filterConfigs"
        @clear="load(clearFilters)"
        @apply="load(applyFilters)" />
    </template>

    <section
      class="relative"
      data-cy="completion-dashboard-page">
      <div
        v-if="isLoading"
        class="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
        <LoadingState label="Loading…" />
      </div>
      <EmptyState
        v-if="!isCardDataAvailable"
        :title="completionUnavailableMessage(unavailableReason)" />
      <EmptyState
        v-else-if="!isLoading && !cardsCompletionData.length"
        title="No completion data yet"
        description="Completion is computed once respondents have started the survey." />
      <template v-else-if="cardsCompletionData.length">
        <CompletionKpis :summary="surveyCompletionData" />
        <div class="mt-6 border-t pt-6">
          <CompletionChart
            :cards="cardsCompletionData"
            :starts="surveyCompletionData.n_started" />
        </div>
        <div class="mt-6 border-t pt-6">
          <CompletionLossesTable
            :cards="cardsCompletionData"
            :starts="surveyCompletionData.n_started" />
        </div>
      </template>
    </section>
  </IndexPageLayout>
</template>
